import { useSnackbar } from 'notistack';
import { useState, useEffect, useRef } from "react";
// @mui
import {
  Box,
  Stack,
  Divider,
  Typography,
  Avatar,
  Chip,
  Badge,
  Fade,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// components
import DIOText from '../../components/DIOText';
import Label from '../../components/Label';
import Iconify from "../../components/Iconify";
import SvgIconStyle from "../../components/SvgIconStyle";
import Image from "../../components/Image";
import FliersDetailStepper from "./FliersDetailStepper";
import { CoffeeChatDialogButton } from './';

// utils
import axios from '../../utils/axios';


const CTAFooter = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'fixed',
  bottom: 0,
  left: 0,
}));

const PrimaryText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.text.primary,
  };
});

// ----------------------------------------------------------------------

export default function FliersDetail({ profile, reload }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmit, setIsSubmit] = useState(false);

  // mobile
  const detailRef = useRef(null);
  const stickyRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [borderVisible, setBorderVisible] = useState(false);

  const hasPlan = user?.employee?.company?.[0]?.scout_plan !== 'no';
  const paidPlan = user?.employee?.company?.[0]?.scout_plan === 'enterprise' || user?.employee?.company?.[0]?.scout_plan === 'pro';
  const canSave = user?.employee?.company?.[0]?.scout_plan === 'enterprise';

  if (profile === undefined) return <></>;

  const toggleSave = async () => {
    try {
      const saved = profile?.is_save;
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      if (saved) {
        await axios.post(`/api/company/${companyId}/fliers/${profile?.id}/unsave/`);
        reload();
        enqueueSnackbar('크루 찜하기가 해제되었습니다', { variant: 'info' });
      } else {
        await axios.post(`/api/company/${companyId}/fliers/${profile?.id}/save/`);
        reload();
        enqueueSnackbar('크루 찜하기 완료되었습니다', { variant: 'success' });
      }
      setIsSubmit(false);
    } catch(err) {
      console.error(err);
      setIsSubmit(false);
    }
  };

  // 포트폴리오 링크
  const urlWithHttps = (url) => {
    if (!url) return ''
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`
    }
    return url
  };

  // 포트폴리오 파일
  const fileOpen = async (item) => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const crewId = profile?.id;
    const response = await axios.post(`/api/company/${companyId}/fliers/${crewId}/files/${item.id}/url/`);
    window.open(response.data.url);
    setIsSubmit(false);
  };

  useEffect(() => {
    const toggleVisibility = () => {
      const currentDetailPosition = detailRef.current.getBoundingClientRect();
      setIsVisible(currentDetailPosition.y < 300);

      const currentStickyPosition = stickyRef.current.getBoundingClientRect();
      setBorderVisible(currentStickyPosition.y === 0);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
}, [isVisible])

  return (
    <Box
      sx={{ width: 1, mt: { xs: 0, sm: 1 }, mb: 10 }}
    >
      {/* fliers detail */}
      <Fade in={!!profile?.id}>
      <Box
        ref={stickyRef}
        sx={{ 
          py: 2, position: "sticky", top: 0, backgroundColor: "common.white", zIndex: 10,
          ...borderVisible && { borderBottom: 1, borderColor: 'divider' },
        }}
      >
      <Stack direction="row" justifyContent="space-between">
        <Stack direction={{xs: "row-reverse", sm: "row"}} spacing={2} sx={{ ...(!smUp && { width: 1 }) }} justifyContent="space-between">
          <Box
            sx={{ display: "flex", alignItems: "start", }}
          >
            <Stack sx={{ height: 1 }} display="flex" justifyContent="space-between">
            <Badge
              sx={{
                width: smUp ? 56 : 48,
                height: smUp ? 56 : 48,
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Image src={"https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/img_verified.png"} sx={{ width: 20, height: 20, mr: 2, mb: 2.5 }} />
              }
            >
              <Avatar
                src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${profile?.randomhash || '5'}.png`}
                alt={profile?.name}
                color={'default'}
                sx={{
                  margin: 'auto',
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: 'common.white',
                  width: smUp ? 56 : 48,
                  height: smUp ? 56 : 48,
                }}
              />
            </Badge>
            </Stack>
          </Box>
          <Box>
           <Stack spacing={1}>
              <Stack>
                <Box>
                  {profile?.main_group && (
                  <Label variant="outlined" color="primary">
                    {profile?.main_group}
                  </Label>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant={smUp ? "h3" : "h4"}>
                    {profile?.main_role || ''}
                  </Typography>
                  <Typography variant={smUp ? "h5" : "subtitle1"} color="text.secondary">
                    {profile?.position_display}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" color="text.secondary">
                    <PrimaryText>{profile?.nickname}</PrimaryText> 크루
                  </Typography>
                  <Divider orientation={"vertical"} sx={{ borderWidth: 1 }} flexItem />
                  <Typography variant="subtitle1" color="text.secondary">
                    {profile?.years}년차
                  </Typography>
                  {smUp && profile?.is_following && canSave && (
                  <Label color="info">
                    우리 회사 팔로우 중
                  </Label>
                  )}
                </Stack>
                {!smUp && profile?.is_following && canSave && (
                <Box sx={{ mt: 0.5 }}>
                  <Label color="info">
                    우리 회사 팔로우 중
                  </Label>
                </Box>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
        {smUp && hasPlan && (
        <Box
          sx={{ display: "flex", alignItems: "end" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            {canSave && (
            <Tooltip title={profile?.is_save ? '찜하기 해제' : '이 크루 찜하기'} placement="top" arrow>
              <IconButton size="large" color="error" disabled={isSubmit} onClick={toggleSave}>
                <Iconify icon={profile?.is_save ? 'eva:heart-fill' : 'eva:heart-outline'} />
              </IconButton>
            </Tooltip>
            )}
            {paidPlan && (
            <CoffeeChatDialogButton profile={profile} reload={reload} freeSolo />
            )}
          </Stack>
        </Box>
        )}
      </Stack>

      </Box>
      </Fade>

      <Fade in={!!profile?.id}>
        <Stack sx={{ my: 3 }}>
          {/* 추천사유 */}
          {profile?.scout_reason && (
          <Stack
            sx={{
              backgroundColor: "grey.100",
              borderRadius: 2,
              p: 3,
              border: "1px solid #919EAB3D",
              mb: 2,
            }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/icon-loudspeaker-mono.svg`} sx={{ color: "info.main", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
              <Typography variant="h6">
                <DIOText />에서 이 크루를 추천한 이유
              </Typography>
            </Stack>
            <Stack spacing={1} sx={{ width: 1, overflow: "hidden" }}>
            {profile?.scout_reason?.split('\n').filter(m => m).map((line, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                <SvgIconStyle src={"/icons/icon-check-mono.svg"} sx={{ minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16, my: 0.5 }} />
                <Typography variant="body2">
                  {line}
                </Typography>
              </Stack>
            ))}
            </Stack>
          </Stack>
          )}

          <Divider />
          <Accordion sx={{ p: 1, '&.MuiAccordion-root' : { my: 0, '&:before': { opacity: 0 } } }}>
            <AccordionSummary
              expandIcon={<Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />}
            >
              <Stack direction="row" alignItems={"center"} spacing={1}>
                <SvgIconStyle src={"/icons/icon-coffee-mono.svg"} sx={{ width: 20, height: 20, color: "text.disabled" }} />
                <Typography variant="subtitle1" paragraph>
                  커피챗은 이렇게 진행돼요.
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ mb: 1, px: 0 }}>
              <FliersDetailStepper />
            </AccordionDetails>
          </Accordion>
          <Divider />
        </Stack>
      </Fade>

      <Fade in={!!profile?.id}>
      <Box
        sx={{ py: 2, px: { xs: 0, sm: 2 } }}
        id="crew-profile"
        ref={detailRef}
      >
        <Typography variant="h5">
          크루 상세 정보
        </Typography>

        <Stack spacing={1} sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-shield-user-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              세부 포지션
            </Typography>
          </Stack>
          <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap' }} spacing={1}>
            {profile?.main_role && (
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', gap: 1, }}>
                <Typography variant="subtitle1" sx={{ color: "text.secondary" }} whiteSpace="nowrap">메인</Typography>
                <Chip label={profile?.main_role} sx={{ backgroundColor: "grey.200", color: "text.secondary" }} />
              </Stack>
            )}
            {profile?.sub_roles?.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', gap: 1, }}>
                <Typography variant="subtitle1" sx={{ color: "text.secondary" }} whiteSpace="nowrap">서브</Typography>
                {profile?.sub_roles?.map((r, index) => (
                  <Chip key={index} label={r} sx={{ backgroundColor: "grey.200", color: "text.secondary" }} />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>

        {profile?.categories?.length > 0 && (
        <Stack spacing={1} sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/ic_trophy.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              경력 카테고리
            </Typography>
          </Stack>
          <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap' }}>
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              {profile?.categories?.map((c, index) => (
                <Chip key={index} label={c} />
              ))}
            </Stack>
          </Stack>
        </Stack>
        )}

        <Stack spacing={1} sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              크루 경력 정보
            </Typography>
          </Stack>
          <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap' }} spacing={2} divider={<Divider />}>
            {profile?.careers?.map((c, index) => (
              <Stack key={index} spacing={1} alignItems="baseline">
                <Typography variant="subtitle1" sx={{ wordBreak: "break-all" }}>{c?.name}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }} whiteSpace="nowrap">{c?.period}</Typography>
                {c?.description && (<Typography variant="body1" sx={{ wordBreak: "break-all", whiteSpace: 'pre-wrap' }}>{c?.description}</Typography>)}
              </Stack>
            ))}
          </Stack>
        </Stack>

        {profile?.intro && (
        <Stack spacing={1} sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              크루 소개
            </Typography>
          </Stack>
          <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} spacing={2}>
            <Typography variant="body1">
              {profile?.intro}
            </Typography>
          </Stack>
        </Stack>
        )}

        {/* portfolio 는 나중에 */}
        {/* {(profile?.portfolio?.files?.length > 0 || profile?.portfolio?.urls?.length > 0) && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 4, mb: 1 }}>
              <SvgIconStyle src={`/icons/icon-clip-mono.svg`} sx={{ color:  "secondary.main" }} />
              <Typography variant="h6">
                크루 포트폴리오
              </Typography>
            </Stack>

            <Box sx={{ pl: 4 }}>
            {profile.portfolio?.urls?.map((u, index) => (
              <Stack direction="row" alignItems="center" sx={{ pl: 0.5 }} key={index}>
                <Link
                  href={urlWithHttps(u.url)}
                  underline="none"
                  color="text.secondary"
                  fontWeight="bold"
                  target="_blank"
                  variant="body2"
                >
                  {profile?.nickname} 포트폴리오 링크
                </Link>
              </Stack>
            ))}
            {profile.portfolio?.files?.map((f, index) => (
              <LoadingButton
                key={index}
                sx={{ textTransform: 'none', color: 'text.secondary', ml: index === 0 ? 0 : 0.5, textDecoration: "underline" }}
                onClick={() => fileOpen(f)}
                loading={isSubmit}
              >
                {profile?.nickname} 포트폴리오
              </LoadingButton>
            ))}
            <Stack direction="row" alignItems="center">
              <IconStyle icon={"eva:alert-triangle-fill"} sx={{ color: 'warning.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                {smUp && '크루의 포트폴리오가 '}외부에 유출되지 않게 주의해주세요.
              </Typography>
            </Stack>
            </Box>
          </>
        )} */}

        {profile?.employment_type && (
        <Stack spacing={1} sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Iconify icon={'eva:sun-outline'} sx={{ width: 24, height: 24, color: "secondary.main" }} />
            <Typography variant="h6">
              현재 본업 고용 형태
            </Typography>
          </Stack>
          <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} spacing={2}>
            <Typography variant="body1">
              {profile?.employment_type}
            </Typography>
          </Stack>
        </Stack>
        )}

        {profile?.work_place && (
        <Stack spacing={1} sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-building-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="h6">
              현재 본업 근무 장소
            </Typography>
          </Stack>
          <Stack sx={{ pl: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} spacing={2}>
            <Typography variant="body1">
              {profile?.work_place}
            </Typography>
          </Stack>
        </Stack>
        )}

      </Box>
      </Fade>

      {!smUp && hasPlan && (
        <CTAFooter
          sx={{
            display: isVisible ? 'block' : 'none',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              gap: 2,
              width: '100%',
              px: 3,
              py: 1,
              mb: 2,
              backgroundColor: 'background.paper',
             }}
          >
            {canSave && (
            <IconButton size="medium" color="error" disabled={isSubmit} onClick={toggleSave}>
              <Iconify icon={profile?.is_save ? 'eva:heart-fill' : 'eva:heart-outline'} />
            </IconButton>
            )}
            {paidPlan && (
            <CoffeeChatDialogButton profile={profile} reload={reload} freeSolo />
            )}
          </Box>
        </CTAFooter>
      )}
    </Box>
  )
}
